import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type Filters = Record<string, string | number | undefined>;

export const useUrlFilters = <T extends Filters = Filters>(defaults: T) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Convert URL → object
  const getFiltersFromUrl = (): T => {
    const searchParams = new URLSearchParams(location.search);
    const entries: [string, string][] = Array.from(searchParams.entries());

    const parsed: Record<string, unknown> = { ...defaults };
    for (const [key, value] of entries) {
      const defaultVal = defaults[key as keyof T];

      if (typeof defaultVal === "number") {
        parsed[key] = Number(value);
      } else {
        parsed[key] = value;
      }
    }

    return {
      ...(defaults || {}),
      ...parsed,
    } as T;
  };

  const [filters, setFilters] = useState<T>(getFiltersFromUrl);

  // Update filters & sync to URL
  const updateFilters = (newFilters: Partial<T>) => {
    const merged = { ...filters, ...newFilters };

    const params = new URLSearchParams();

    Object.entries(merged).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        params.set(key, String(value));
      }
    });

    navigate({ search: params.toString() }, { replace: true });
    setFilters(merged);
  };

  // Whenever URL changes, sync state
  useEffect(() => {
    setFilters(getFiltersFromUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return { filters, updateFilters };
};
